import _ from 'underscore';

import { SignInUrlBuilder } from '@tableau/shared-urls';

import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Helpers } from '../../ts/helpers/Helpers';
import { SiteUrlNameCapturingRegex } from '../../ts/services/SiteUrlMatcherFactory';
import { WindowLocationService } from '../../commonInfra/services/WindowLocationService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { AuthenticationTypeResolutionService } from './AuthenticationTypeResolutionService';
import { IGetSiteSamlLoginInfoResult } from '~src/ts/ServerApi/types/IGetSiteSamlLoginInfoResult';

export interface IExternalLoginEntryPoint {
  path: string;
  queryParams: _.Dictionary<any>;
}

export namespace ExternalAuthService {

  const SamlLoginUrl: string = 'vizportal/api/saml/login';
  const OpenIdLoginUrl: string = 'vizportal/api/web/v1/auth/openId';

  export function getLoginUrl(loginParams: SignInUrlBuilder.QueryParams = {}): string {
    const entryPoint = getExternalLoginEntryPoint(loginParams, true);
    const url = getEntryPointUrl(entryPoint);
    return url;
  }

  export function login(loginParams: SignInUrlBuilder.QueryParams = {}, siteSamlInfo?: IGetSiteSamlLoginInfoResult) {
    const entryPoint = getExternalLoginEntryPoint(loginParams, false, siteSamlInfo && buildExternalLoginEntryPoint(siteSamlInfo));
    const url = getEntryPointUrl(entryPoint);
    WindowLocationService.navigateToWithoutAddingToHistory(url);
  }

  function buildExternalLoginEntryPoint(getSiteSamlLoginInfoResult: IGetSiteSamlLoginInfoResult): IExternalLoginEntryPoint {
    return {
      path: null,
      queryParams: {
        authSetting: getSiteSamlLoginInfoResult ? getSiteSamlLoginInfoResult.authSettings : 'SAML',
        embedded: getSiteSamlLoginInfoResult ? getSiteSamlLoginInfoResult.embeddedAuth : false,
        idp: ServerSettingsUnauthenticated.getSamlServiceEntityId(),
        sendPodInfo: false,
        siteLuid: getSiteSamlLoginInfoResult ? getSiteSamlLoginInfoResult.siteLuid : '',
        target_site: getSiteSamlLoginInfoResult ? getSiteSamlLoginInfoResult.urlNamespace : ''
      }
    };
  }

  function getExternalLoginEntryPoint(loginParams: SignInUrlBuilder.QueryParams, frontDoor: boolean,
            entryPoint?: IExternalLoginEntryPoint): IExternalLoginEntryPoint {
    if (ServerSettingsUnauthenticated.isSiteSamlEnabled() && ServerSettingsUnauthenticated.isServerDomainFamilyLocal()) {
      return {
        path: SamlLoginUrl,
        queryParams: frontDoor ? {} : getSiteSamlLoginQueryParams(entryPoint, loginParams)
      };
    } else if (AuthenticationTypeResolutionService.isAuthenticationSaml()) {
      return {
        path: SamlLoginUrl,
        queryParams: frontDoor ? {} : getSamlLoginQueryParams(loginParams)
      };
    } else if (AuthenticationTypeResolutionService.isAuthenticationOpenId()) {
      return {
        path: OpenIdLoginUrl,
        queryParams: frontDoor ? {} : getOpenIdLoginQueryParams(loginParams)
      };
    } else {
      throw new Error("This server doesn't use a supported authentication type");
    }
  }

  // params are a subset of those supported by SAMLExtendedEntryPoint.java and SAMLExtendedProcessingFilter.java (via RelayState)
  function getSamlLoginQueryParams(loginParams: SignInUrlBuilder.QueryParams): _.Dictionary<any> {
    // The 'sendPodInfo' parameter ensures that the server doesn't try to redirect the close-pop-up page to
    // the correct POD for the site. Instead, it redirects to a different page on the destination
    // POD which can send the POD info back to the parent window.

    const queryParams: _.Dictionary<any> = {
      dest:        getRedirect(loginParams),
      target_site: getSiteUrlName(loginParams),
      sendPodInfo: getBooleanQueryParam(UrlParamKeys.ClosePopupWhenDone),
      authSetting: getStringQueryParam(UrlParamKeys.AuthSetting),
      siteLuid:    getStringQueryParam(UrlParamKeys.SiteLuid),
      embedded:    getBooleanQueryParam(UrlParamKeys.Embedded)
    };
    if (queryParams['target_site'] == null) {
      // empty target_site has special meaning for the SAML entry point: it refers to the Default site
      delete queryParams['target_site'];
    }
    return queryParams;
  }

  function getSiteSamlLoginQueryParams(entryPoint:  IExternalLoginEntryPoint,
      loginParams: SignInUrlBuilder.QueryParams): _.Dictionary<any> {
    const queryParams: _.Dictionary<any> = entryPoint ? entryPoint.queryParams : {};
    if (!queryParams['target_site']) {
      // empty target_site has special meaning for the SAML entry point: it refers to the Default site
      delete queryParams['target_site'];
    }
    queryParams['dest'] = getRedirect(loginParams);
    return queryParams;
  }

  // params are a subset of those supported by openIdInitLoginHandler() in WebClientGetAuthenticationController.java
  function getOpenIdLoginQueryParams(loginParams: SignInUrlBuilder.QueryParams): _.Dictionary<string> {
    return {
      path: getRedirect(loginParams),
      siteUrlName: getSiteUrlName(loginParams)
    };
  }

  function getRedirect(loginParams: SignInUrlBuilder.QueryParams): string {

    if (loginParams.externalRedirect) {
      return loginParams.externalRedirect;
    } else if (loginParams.redirect) {
      return WindowLocationService.getPathQuery() + '#' + loginParams.redirect;
    } else if (getBooleanQueryParam(UrlParamKeys.ClosePopupWhenDone)) {
      return '/' + ServerSettingsUnauthenticated.serverLanguage() + '/closePopup.html';
    } else {
     return '/';
    }
  }

  function getSiteUrlName(loginParams: SignInUrlBuilder.QueryParams): string {
    if (_.isString(loginParams.site)) {
      return loginParams.site;
    } else if (_.isString(loginParams.target_site)) {
      return loginParams.target_site;
    } else if (_.isString(loginParams.redirect)) {
      const match = loginParams.redirect.match(SiteUrlNameCapturingRegex);
      return match && match[1];
    } else {
      return null;
    }
  }

  function getEntryPointUrl(entryPoint: IExternalLoginEntryPoint): string {
    let url = entryPoint.path;
    const paramJoinSymbol = (url.indexOf('?') >= 0) ? '&' : '?';
    const queryParams = Helpers.serialize(entryPoint.queryParams);
    if (queryParams.length > 0) {
      url += paramJoinSymbol + queryParams;
    }
    return url;
  }

  function getBooleanQueryParam(key: string): boolean {
    return Helpers.BooleanQueryParamRegex.test(getStringQueryParam(key));
  }

  function getStringQueryParam(key: string): string {
    const urlParams = <{[key: string]: string}>WindowLocationService.getHashQueryObject();
    return urlParams[key];
  }

}

export type ExternalAuthService = typeof ExternalAuthService;